import { getToken } from "@/utils/auth";
import React, { lazy, Suspense, useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { usePermission } from "@/core/permissions/hooks";
import { getRoutePermission } from "@/core/permissions/route-map";
import { useStore } from "@/store";

// Duyuru modalı ayrı chunk: login/ilk açılış bundle'ına girmesin.
const LoginAnnouncementsModal = lazy(
  () =>
    import(
      "@/features/client-announcements/components/LoginAnnouncementsModal"
    ),
);

const BYPASS_TOKEN_CHECK = false;

/** Sadece yöneticilerin erişebildiği path'ler (client bu path'lere erişemez) */
const ADMIN_ONLY_PATHS = [
  "/staff-management",
  "/roles-and-permissions",
  "/education-management",
  "/request-and-approval",
  "/intern-management",
  "/black-list",
  "/announcements",
  "/logs",
  "/reports",
  "/support-tickets",
  "/items",
  "/sms-broadcast",
  "/mail-broadcast",
  "/education-calendar",
];

const isAdminOnlyPath = (path: string): boolean =>
  ADMIN_ONLY_PATHS.some((p) => path === p || path.startsWith(p + "/"));

const ProtectedRoute: React.FC = () => {
  const token = getToken();
  const location = useLocation();
  const isStaff = useStore((state) => state.isStaff);
  const { hasPermission } = usePermission();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [hasRoutePermission, setHasRoutePermission] = useState<boolean | null>(
    null,
  );

  const parseTokenPayload = (token: string) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Token parsing error:", error);
      return null;
    }
  };

  const removeCookieToken = () => {
    try {
      const hostname = window.location.hostname;
      const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
      const domainParts = hostname.split(".");
      const isSubdomain = domainParts.length > 2;

      let cookieString =
        "token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";

      if (!isLocalhost && isSubdomain) {
        cookieString += `; domain=.${domainParts.slice(-2).join(".")}`;
      } else if (isLocalhost) {
        cookieString += "; domain=localhost";
      }

      document.cookie = cookieString;
      document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";

      let refreshCookieString =
        "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";

      if (!isLocalhost && isSubdomain) {
        refreshCookieString += `; domain=.${domainParts.slice(-2).join(".")}`;
      } else if (isLocalhost) {
        refreshCookieString += "; domain=localhost";
      }

      document.cookie = refreshCookieString;
      document.cookie =
        "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";
    } catch (error) {
      console.error("Error removing token cookies:", error);
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      // Token kontrolünü bypass et
      if (BYPASS_TOKEN_CHECK) {
        console.log(
          "🔒 ProtectedRoute: Token check bypassed (development mode)",
        );
        setIsValidToken(true);
        setHasRoutePermission(true);
        return;
      }

      if (!token) {
        console.log("🔒 ProtectedRoute: No token found, redirecting to login");
        setIsValidToken(false);
        return;
      }

      const payload = parseTokenPayload(token);
      if (!payload) {
        console.log("🔒 ProtectedRoute: Invalid token payload");
        setIsValidToken(false);
        return;
      }

      const exp = payload.exp;
      if (exp && Date.now() >= exp * 1000) {
        console.log("🔒 ProtectedRoute: Token expired");
        setIsValidToken(false);
        return;
      }

      console.log("🔒 ProtectedRoute: Token is valid");
      setIsValidToken(true);
    };

    validateToken();
  }, [token]);

  useEffect(() => {
    if (isValidToken === false) {
      removeCookieToken();
    }
  }, [isValidToken]);

  useEffect(() => {
    if (!isValidToken) return;

    const currentPath = location.pathname.replace("/dashboard", "");

    // Root path veya unauthorized sayfası her zaman erişilebilir
    if (currentPath === "/" || currentPath === "/unauthorized") {
      setHasRoutePermission(true);
      return;
    }

    // Path için gerekli permission'ı bul
    const permission = getRoutePermission(currentPath);

    // Permission yoksa erişime izin ver (public route)
    if (!permission) {
      setHasRoutePermission(true);
      return;
    }

    // Token bypass aktifse permission kontrolünü de bypass et
    if (BYPASS_TOKEN_CHECK) {
      setHasRoutePermission(true);
      return;
    }

    // Permission kontrolü
    setHasRoutePermission(hasPermission(permission));
  }, [location.pathname, isValidToken]);

  // Client (isStaff=false) admin path'e erişmeye çalışırsa client dashboard'a yönlendir
  const currentPathForRole =
    location.pathname.replace(/^\/dashboard/, "") || "/";
  if (!isStaff && isAdminOnlyPath(currentPathForRole)) {
    console.log(
      "🔒 ProtectedRoute: Client tried to access admin route, redirecting to client-dashboard",
    );
    return <Navigate to="/dashboard/client-dashboard" replace />;
  }

  // Bypass aktifse direkt erişime izin ver
  if (BYPASS_TOKEN_CHECK) {
    console.log("🔒 ProtectedRoute: Bypass active - Access granted");
    return <Outlet />;
  }

  // Loading state - show nothing while checking token
  if (isValidToken === null) {
    console.log("🔒 ProtectedRoute: Checking token validity...");
    return null;
  }

  // If token is invalid, redirect to login
  if (!isValidToken) {
    console.log("🔒 ProtectedRoute: Redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // If checking route permission, wait
  if (hasRoutePermission === null) {
    console.log("🔒 ProtectedRoute: Checking route permissions...");
    return null;
  }

  // If no permission for route, redirect to unauthorized
  if (!hasRoutePermission) {
    console.log(
      "🔒 ProtectedRoute: No permission, redirecting to unauthorized",
    );
    return <Navigate to="/dashboard/unauthorized" replace />;
  }

  console.log("🔒 ProtectedRoute: Access granted");
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <LoginAnnouncementsModal />
      </Suspense>
    </>
  );
};

export default ProtectedRoute;
